import store from "../redux/store";
import moment from "moment-timezone";
import {TIME_LIST} from "./Util";
import Constants from "../Constants";


class DateUtil {

    DATE_TIME_CONVERSION_FORMAT = "YYYY-MM-DDTHH:mm:ss"; // for internal usage for date conversion

    getDefaultConfigs() {
        const customerRedux = store.getState()?.customer_bootstrap_data?.value;
        return {
            original_date_format: customerRedux?.originalDateFormat?.toUpperCase(),
            timezone: customerRedux?.user?.timeZone,
            date_format: customerRedux?.dateFormatInDays?.toUpperCase(),
            month_date_format: customerRedux?.monthDayDateFormat?.toUpperCase(),
            date_time_format: customerRedux?.dateFormatInDays?.toUpperCase() + " h:mm A", // we don't have format for date time, so using user's date format + temporary time format for now
        }
    }

    getDateTimestamp(date) {
        if (date) {
            if (date?.utcTimestamp) {
                return date?.utcTimestamp;
            } else if (date instanceof Date) {
                return date.getTime();
            } else if (moment.isMoment(date)) {
                return date.valueOf();
            } else {
                return date;
            }
        }
    }

    combinedConfigs(configs) {
        // internal method
        let defaultConfigs = this.getDefaultConfigs();
        switch (configs.format) {
            case null:
            case undefined:
            case DATE_FORMATS.DATE:
                configs.format = defaultConfigs.date_format;
                break;
            case DATE_FORMATS.MONTH_DAY:
                configs.format = defaultConfigs.month_date_format;
                break;
            case DATE_FORMATS.DATE_TIME:
            case DATE_FORMATS.SHORT_DATE_TIME:
                configs.format = defaultConfigs.date_time_format;
                break;
            default:
                break;
        }
        if (configs.timezone === undefined) { // null is a valid value
            configs.timezone = defaultConfigs.timezone;
        }        
        configs.original_date_format = defaultConfigs.original_date_format
        return configs;
    }

    formatWithoutConversion(date, format, timezone) {
        // date is required, if date is not provided, return empty string
        // format is optional, if format is not provided, use default format from user settings
        // timezone is optional, if timezone is not provided, use user timezone, if timezone is null, no timezone conversion
        if (!date) return "";

        let configs = this.combinedConfigs({format, timezone});
        if (this.isServerObject(date) && !date.isServerTimestamp) {            
            // isServerTimestamp is a flag which indicates date is in server timezone but is not generated by server, hence it doesn't have the formatted fields
            return this.serverFormat(date, configs.format, configs.timezone);
        } else if (typeof this.getDateTimestamp(date) === 'number') {        
            let momentDate = moment(this.getDateTimestamp(date));            
            return momentDate.format(configs.original_date_format);
        } else if (typeof date === "string") {            
            let momentDate = moment(date);                        
            return momentDate.format(configs.format);
        } else {            
            return "";
        }
    }

    format(date, format, timezone) {
        // date is required, if date is not provided, return empty string
        // format is optional, if format is not provided, use default format from user settings
        // timezone is optional, if timezone is not provided, use user timezone, if timezone is null, no timezone conversion
        if (!date) return "";

        let configs = this.combinedConfigs({format, timezone});
        if (this.isServerObject(date) && !date.isServerTimestamp) {            
            // isServerTimestamp is a flag which indicates date is in server timezone but is not generated by server, hence it doesn't have the formatted fields
            return this.serverFormat(date, configs.format, configs.timezone);
        } else if (typeof this.getDateTimestamp(date) === 'number') {            
            let momentDate = moment(this.getDateTimestamp(date));            
            if (configs.timezone) {
                momentDate = momentDate.tz(configs.timezone);                
            }
            return momentDate.format(configs.format);
        } else if (typeof date === "string") {            
            let momentDate = moment(date);            
            if (configs.timezone) {
                momentDate = moment.tz(date, configs.timezone);                
            }
            return momentDate.format(configs.format);
        } else {            
            return "";
        }
    }

    serverFormat(date, format, timezone) {
        // internal method, use format() for usage outside DateUtil class
        let defaultConfigs = this.getDefaultConfigs();
        switch (format) {
            case null:
            case undefined:
            case defaultConfigs.date_format:
            case DATE_FORMATS.DATE:
                return date.date;
            case this.getDefaultConfigs().date_time_format:
            case DATE_FORMATS.DATE_TIME:
                return date.dateWithTime;
            case DATE_FORMATS.SHORT_DATE_TIME:
                return date.date + " " + date.timeOfDay;
            case DATE_FORMATS.MONTH_DAY:
            case defaultConfigs.month_date_format:
                return date.monthAndDay;
            case DATE_FORMATS.MONTH_YEAR:
                return date.monthAndYear;
            case DATE_FORMATS.TIME_OF_DAY:
                return date.timeOfDay;
            case DATE_FORMATS.RELATIVE_TO_NOW:
                return date.relativeToNow;
            case DATE_FORMATS.NUM_OF_DAYS_FROM_TODAY:
                return date.numOfDaysFromToday;
            case DATE_FORMATS.IS_TODAY:
                return date.today;
            case DATE_FORMATS.IS_YESTERDAY:
                return date.yesterday;
            default:
                return this.format(date.utcTimestamp, format, timezone);
        }
    }

    isServerObject(obj) {
        return obj && typeof obj === "object" && obj.utcTimestamp && (obj.dateWithTime || obj.isServerTimestamp);
    }

    isToday(momentDate, timezone) {
        return momentDate.isSame(moment().tz(timezone), 'day');
    }

    isOverDue(momentDate, timezone) {
        return momentDate.isBefore(moment().tz(timezone), 'day');
    }

    pendingStatus(date, timezone) {
        if (typeof date === "object" && date.utcTimestamp) {
            date = date.utcTimestamp;
        }
        timezone = this.combinedConfigs({timezone}).timezone;
        const momentDate = moment(date).tz(timezone);
        if (this.isToday(momentDate, timezone)) {
            return "Today";
        }
        if (this.isOverDue(momentDate, timezone)) {
            return "Over Due";
        }
        return "";
    }

    dateTimestampObjectTransformation(obj, keys) {
        // this function will transform the date object inside the given object to {utcTimestamp: <timestamp>}
        // keys is optional, if keys is not provided, it will look for keys that ends with "date" (case ignored)
        // this function is useful when sending the object to the server, as the server expects the date in timestamp object format for api version >= 1.25
        return JSON.parse(JSON.stringify(obj, (key, value) => {
            if (value && ((keys && keys.includes(key)) || (!keys && key.match(/date$/i)))) {
                let timestamp = this.getDateTimestamp(value);
                if (typeof timestamp === "number") {
                    return this.timestampObj(timestamp);
                }
            }
            return value;
        }));
    }

    convertToLocalDateTimestamp(date) {
        // this method takes a date and converts it to local timezone while keeping the time same as in server timezone
        if (this.isServerObject(date)) {
            let timestamp = this.getDateTimestamp(date);
            let dateMoment = moment(timestamp).tz(this.getDefaultConfigs().timezone);
            return moment(dateMoment.format(this.DATE_TIME_CONVERSION_FORMAT)).valueOf();
        }
        return this.getDateTimestamp(date);
    }

    convertToServerDateTimestamp(date) {
        // this method takes a date and converts it to server timezone while keeping the time same as in user timezone and returns the timestamp

        if (this.isServerObject(date)) {
            return this.getDateTimestamp(date);
        }
        let timestamp = this.getDateTimestamp(date);
        if (!timestamp || typeof timestamp !== "number") {
            console.error("Invalid date provided for conversion", date);
            return date;
        }
        let dateString = moment(timestamp).format(this.DATE_TIME_CONVERSION_FORMAT);
        let dateMoment = moment.tz(dateString, this.getDefaultConfigs().timezone);
        return dateMoment.valueOf();
    }

    convertToServerDateTsObj(date) {
        if (typeof this.getDateTimestamp(date) === 'number') {
            return this.timestampObj(this.convertToServerDateTimestamp(date), true);
        }
        return date;
    }

    convertToServerMoment(date) {
        // this method takes a date and converts it to server timezone while keeping the time same as in user timezone and returns a moment object
        if (!date) return date;
        let timestamp = this.convertToServerDateTimestamp(date);
        if (timestamp && typeof timestamp === 'number') {
            return moment(timestamp).tz(this.getDefaultConfigs().timezone);
        }
        console.error("Invalid date provided for momentTzForServer", date)
        return moment.tz(date, this.getDefaultConfigs().timezone); // this may work for string, but it may still result in error in different locales.
    }

    dateFormatSubmitOld(date) {
        // this method is used to convert the date to the format that is used in the old api versions 1.24
        return this.format(date, this.DATE_TIME_CONVERSION_FORMAT, null);
    }

    convertAndFormatForSubmitOld(date) {
        return this.dateFormatSubmitOld(this.convertToServerDateTimestamp(date));
    }

    timestampObj(date, isServerTimestamp) {
        // isServerTimestamp is an optional flag to indicate that the given timestamp is already in server timezone, default value false
        let timestamp = this.getDateTimestamp(date);
        if (typeof timestamp === 'number') {
            return {utcTimestamp: timestamp, isServerTimestamp: isServerTimestamp || false};
        }
        console.error("Invalid date for timestamp object", date);
        return date;
    }

    getTimeDropdownVal(time) {
        if (typeof time !== "number" || time > 24) {
            time = this.getDateTimestamp(time);
            time = moment(time).hours();
        }
        return TIME_LIST.find((option) => option?.value === time)
    }

    getPSTStartOfDay(date) {
        let startDateString = moment(date).format(Constants.DATE_FORMAT_YYYY_MM_DD_HH_MM_SS);
        return moment.tz(startDateString, Constants.TIMEZONE_PST).startOf('day').valueOf();
    }

    getPSTEndOfDay(date) {
        let endDateString = moment(date).format(Constants.DATE_FORMAT_YYYY_MM_DD_HH_MM_SS);
        return moment.tz(endDateString, Constants.TIMEZONE_PST).endOf('day').valueOf();
    }

}

export default new DateUtil();

/**
 * These date formats are just for reference, the actual result may vary based on the user configurations, i.e. timezone and localization.
 * The intention is to create enums for the date formats in DateUtil.format() and provide example formats.
 * check combinedConfigs for replacement rules
 */
export const DATE_FORMATS = {
    DATE: "M/D/YY", // default, this format will be replaced
    DATE_TIME: "M/D/YY h:mm:ss A z", // this format will be replaced
    SHORT_DATE_TIME: "M/D/YY h:mm A", // this format will be replaced
    MONTH_DAY: "M/D", // this format will be replaced
    MONTH_YEAR: "MM/YYYY",
    TIME_OF_DAY: "h:mm A",
    RELATIVE_TO_NOW: "relative_to_now",
    NUM_OF_DAYS_FROM_TODAY: "num_of_days_from_today",
    IS_TODAY: "today",
    IS_YESTERDAY: "yesterday",
}